import { useCallback } from "react";
import { useHttpAdapter } from "./useHttpAdapter";
import { MonitoringConfig } from "../types/add-brand-types";
import { isNotEmpty } from "../utils/type-utils";
import {
  GenerateDraftReplyResponse,
  SocialPostsMonitoring,
} from "../types/growth";

interface UpdateKeywordResponse {
  status: string;
  monitoring_config: MonitoringConfig;
}

export const useGrowthApi = () => {
  const { post, get } = useHttpAdapter();

  const updateMonitoring = useCallback(
    async (
      keywords: string[],
      type: string,
      productId: string,
      platforms?: string[]
    ) => {
      try {
        const payload = {
          values: keywords,
          ...(isNotEmpty(platforms) ? { monitoring_platforms: platforms } : {}),
        };

        const response = await post<UpdateKeywordResponse>(
          `/socialmonitoring/update/${type}/${productId}`,
          payload
        );

        if (response.status === 200) {
          return response.data;
        }
        return null;
      } catch (e) {
        return null;
      }
    },
    [post]
  );

  const getSocialListeningPosts = useCallback(
    async (productId: string) => {
      try {
        const response = await get<SocialPostsMonitoring[]>(
          `/socialmonitoring/get/${productId}?offset=0&limit=20&type=social`
        );

        if (response.status === 200) {
          return response.data;
        }
        return [];
      } catch (e) {
        return [];
      }
    },
    [get]
  );

  const getCompetitorReviews = useCallback(
    async (productId: string) => {
      try {
        const response = await get<SocialPostsMonitoring[]>(
          `/socialmonitoring/get/${productId}?offset=0&limit=20&type=competitor_review`
        );

        if (response.status === 200) {
          return response.data;
        }
        return [];
      } catch (e) {
        return [];
      }
    },
    [get]
  );

  const markAsReplied = useCallback(
    async (postId: string) => {
      try {
        const response = await get<any>(`/composer/markAsReplied/${postId}`);
        if (response.status === 200) {
          return true;
        }
        return false;
      } catch (e) {
        return false;
      }
    },
    [get]
  );

  const generateDraftReply = useCallback(
    async (
      productId: string,
      content: string,
      type: string,
      platform: string
    ) => {
      try {
        const payload = {
          product_id: productId,
          source_content: content,
          mode: type,
          platform: platform,
        };
        const response = await post<GenerateDraftReplyResponse>(
          `/composer/generate`,
          payload
        );
        if (response.status === 200) {
          return response.data;
        }
        return null;
      } catch (e) {
        return null;
      }
    },
    [post]
  );

  return {
    updateMonitoring,
    getSocialListeningPosts,
    getCompetitorReviews,
    markAsReplied,
    generateDraftReply,
  };
};
